import { useState, useEffect } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { AdminReferralStatusBadge } from "@/components/admin/AdminReferralStatusBadge";
import { MatchScoreDisplay } from "@/components/ui/MatchScoreDisplay";
import { CVReviewTab } from "./tabs/CVReviewTab";
import { AIInsightsTab } from "./tabs/AIInsightsTab";
import { NotesActionsTab } from "./tabs/NotesActionsTab";
import { FileText } from "lucide-react";
import { AdminReferral, ReferralStatus, type Job, type User as UserType } from "@/lib/schema";
import { AdminReferralWithUI } from "@/api/referrals";
import { getJob } from "@/api/jobs";
import { getUserByUid } from "@/api/users";

interface ReferralViewerProps {
  referral: AdminReferralWithUI | null;
  isOpen: boolean;
  onClose: () => void;
  onStatusChange: (referralId: string, newStatus: ReferralStatus) => void;
  onAddNote: (referralId: string, note: string) => void;
}

interface ReferralViewerData {
  job: Job | null;
  referrer: UserType | null;
  candidateName: string;
  candidateSkills: string[];
  jobSkills: string[];
  matchingSkills: string[];
  missingSkills: string[];
}

export function ReferralViewer({ referral, isOpen, onClose, onStatusChange, onAddNote }: ReferralViewerProps) {
  const [newNote, setNewNote] = useState("");
  const [activeTab, setActiveTab] = useState("ai-insights"); // Changed default to AI Insights
  const [showFullCV, setShowFullCV] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [viewerData, setViewerData] = useState<ReferralViewerData | null>(null);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const { toast } = useToast();

  // Load referral data when referral changes
  useEffect(() => {
    if (!referral) {
      setViewerData(null);
      return;
    }

    const loadReferralData = async () => {
      setIsLoadingData(true);
      try {
        const [job, referrer] = await Promise.all([
          getJob(referral.jobId),
          getUserByUid(referral.referrerId)
        ]);

        // Extract skills from various sources
        const jobSkills: string[] = job?.skills || [];
        
        // Skills are stored directly in skillsAnalysis
        const matchingSkills: string[] = referral.skillsAnalysis?.matchedSkills || [];
        const missingSkills: string[] = referral.skillsAnalysis?.missingSkills || [];
        const candidateSkills: string[] = [];
        

        setViewerData({
          job,
          referrer,
          candidateName: referral.candidateName, // Use the pre-computed candidateName from AdminReferralWithUI
          candidateSkills,
          jobSkills,
          matchingSkills,
          missingSkills
        });
      } catch (error) {
        console.error('Error loading referral data:', error);
        // TODO: Integrate with Sentry for error tracking and reporting
        toast({
          title: "Error Loading Data",
          description: "Failed to load referral details. Please try again.",
          variant: "destructive"
        });
      } finally {
        setIsLoadingData(false);
      }
    };

    loadReferralData();
  }, [referral, toast]);

  if (!referral) return null;

  // Use loaded data or fallback values
  const candidateName = viewerData?.candidateName || 'Loading...';
  
  // Get CV URL from candidate data
  const candidate = referral?.candidateData;
  const latestResume = candidate?.resumes?.[0];
  const cvUrl = latestResume?.url;
  
  // Get match score from referral (converts to percentage if needed)
  const getMatchScore = (referral: AdminReferral): number => {
    const score = referral.matchScore ?? 0;
    // Convert decimal scores (0-1) to percentage scores (0-100)
    return score < 1 ? Math.round(score * 100) : Math.round(score);
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const handleAddNote = () => {
    if (newNote.trim()) {
      onAddNote(referral.id, newNote.trim());
      setNewNote("");
    }
  };

  const handleStatusChange = (newStatus: ReferralStatus) => {
    onStatusChange(referral.id, newStatus);
  };


  const handleDownloadCV = async () => {
    if (!cvUrl) {
      // TODO: Integrate with Sentry for error tracking and reporting
      toast({
        title: "No CV Available",
        description: "This candidate hasn't uploaded a CV yet.",
        variant: "destructive"
      });
      return;
    }
    
    setIsDownloading(true);
    try {
      // Open the CV in a new tab
      window.open(cvUrl, '_blank', 'noopener,noreferrer');
      
      toast({
        title: "CV Opened",
        description: `${candidateName}'s CV has been opened in a new tab.`
      });
    } catch (error) {
      console.error('Error opening CV:', error);
      // TODO: Integrate with Sentry for error tracking and reporting
      toast({
        title: "Failed to Open CV",
        description: "Failed to open CV in new tab. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleFullCVView = () => {
    setShowFullCV(true);
  };



  return (
    <>
      {/* Global Styles for Modal */}
      <style>{`
        .referral-viewer-wrapper {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .referral-viewer-wrapper::-webkit-scrollbar {
          display: none;
        }
        
        /* Additional scrollbar hiding for all browsers */
        .referral-viewer-wrapper *::-webkit-scrollbar {
          display: none;
        }
        .referral-viewer-wrapper * {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        
        /* Specifically target all scrollable areas in the modal */
        .referral-viewer-wrapper .overflow-y-auto,
        .referral-viewer-wrapper .overflow-x-auto,
        .referral-viewer-wrapper .overflow-auto {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .referral-viewer-wrapper .overflow-y-auto::-webkit-scrollbar,
        .referral-viewer-wrapper .overflow-x-auto::-webkit-scrollbar,
        .referral-viewer-wrapper .overflow-auto::-webkit-scrollbar {
          display: none;
        }

        /* Force z-index and pointer events for Select components */
        [data-radix-select-content],
        [data-radix-select-viewport],
        [data-radix-select-item],
        [data-radix-portal] {
          z-index: 200 !important;
          pointer-events: auto !important;
        }

        /* Prevent scroll on Select trigger focus */
        .dialog-content-wrapper [data-radix-select-trigger],
        .dialog-content-wrapper [data-radix-select-trigger]:focus,
        .dialog-content-wrapper [data-radix-select-trigger]:focus-visible {
          scroll-margin: 0 !important;
          scroll-padding: 0 !important;
          scroll-behavior: auto !important;
        }
      `}</style>

      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent 
          className="referral-viewer-wrapper dialog-content-wrapper max-w-6xl max-h-[90vh] overflow-y-auto"
          onPointerDownOutside={(e) => {
            // Allow clicks on Radix Select components (which render in portals)
            const target = e.target as HTMLElement;
            if (
              target.closest('[data-radix-select-content]') ||
              target.closest('[data-radix-select-viewport]') ||
              target.closest('[data-radix-select-item]') ||
              target.closest('[data-radix-portal]')
            ) {
              e.preventDefault();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle className="space-grotesk text-xl font-bold">Referral Details</DialogTitle>
            <DialogDescription className="text-gray-600">
              Review candidate information, CV details, AI insights, and manage referral status.
            </DialogDescription>
          </DialogHeader>
        
        <div className="grid gap-6 py-4">
          {/* Header Section */}
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <Avatar className="h-16 w-16">
                <AvatarImage src={undefined} />
                <AvatarFallback className="text-lg space-grotesk">
                  {getInitials(candidateName)}
                </AvatarFallback>
              </Avatar>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 space-grotesk">{candidateName}</h2>
                <p className="text-gray-600">{referral.candidateEmail}</p>
                  <div className="flex items-center gap-2 mt-2">
                  <AdminReferralStatusBadge status={referral.status} size="md" />
                  <MatchScoreDisplay
                    matchScore={getMatchScore(referral)}
                    recommendation={referral.recommendation}
                    size="md"
                    showLabel={true}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Tabs Content */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full grid-cols-3 space-grotesk">
              <TabsTrigger value="ai-insights">AI Insights</TabsTrigger>
              <TabsTrigger value="cv-review">CV Review</TabsTrigger>
              <TabsTrigger value="notes">Notes & Actions</TabsTrigger>
            </TabsList>

            <TabsContent value="ai-insights">
              <AIInsightsTab
                referral={referral}
                viewerData={viewerData}
                isLoadingData={isLoadingData}
                getMatchScore={getMatchScore}
              />
            </TabsContent>

            <TabsContent value="cv-review">
              <CVReviewTab 
                referral={referral}
                viewerData={viewerData}
                isLoadingData={isLoadingData}
                isDownloading={isDownloading}
                onDownloadCV={handleDownloadCV}
                onFullCVView={handleFullCVView}
              />
            </TabsContent>

            <TabsContent value="notes">
              <NotesActionsTab 
                referral={referral}
                newNote={newNote}
                onNewNoteChange={setNewNote}
                onAddNote={handleAddNote}
                onStatusChange={handleStatusChange}
              />
            </TabsContent>
          </Tabs>
        </div>
        </DialogContent>
      </Dialog>
      
      {/* Full CV View Dialog */}
      <Dialog open={showFullCV} onOpenChange={setShowFullCV}>
        <DialogContent className="max-w-[65vw] max-h-[95vh] w-full h-full p-0">
          {/* Header Bar */}
          <div className="flex items-center justify-between bg-gray-50 border-b p-4">
            <div>
              <DialogTitle className="space-grotesk text-lg font-bold">
                {candidateName} - CV Review
              </DialogTitle>
              <DialogDescription className="space-grotesk text-sm text-gray-600">
                {latestResume?.fileName || 'Resume.pdf'}
              </DialogDescription>
            </div>
          </div>
          {/* CV Viewer Area - Full Canvas */}
          <div className="flex-1 overflow-hidden">
            {cvUrl ? (
              <iframe
                src={cvUrl}
                className="w-full h-[calc(95vh-80px)]"
                title={`CV for ${candidateName}`}
                style={{ border: 'none' }}
              />
            ) : (
              <div className="flex items-center justify-center h-[calc(95vh-80px)] bg-gray-50">
                <div className="text-center">
                  <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 space-grotesk mb-2">
                    No CV Available
                  </h3>
                  <p className="text-gray-600 space-grotesk">
                    No CV document is available for this candidate.
                  </p>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}